import EventEmitter from 'events';
import { Component } from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import { connect } from 'react-redux';
import BEM from '../bem';
import mobxInjectSelect from '../utils/mobxInjectSelect';
import { AlertCard } from '../../widgets/messenger/components/AlertCard';
import { ReduxState } from '../../redux-stores/index';
import waitForElement from '../utils/waitForElement';
import getLimitedMessages from './utils/getLimitedMessages';
import ConversationTasksLoader from './ConversationTasksLoader';
import { DotsIndicator, GroupedMessagesList, ScrollerStickToBottom } from './';
import type { Conversation, Group, Message } from 'types';
import { actions } from 'redux-stores';
const { setSelectedMessageId, setSelectedHighlightedMessageId, setGotoMessageNavigationEvent } =
  actions;

const SELECTED_MESSAGE_HIGHLIGHT_MS = 3000;

const classes = BEM.with('ConversationMessages');

const AUTOSCROLL_AFTER_MOUNT_MS = 100;
const NEW_MESSAGE_INDICATOR = 'NewMessageIndicator';
const THUMB_MARGIN_TOP = 16;

type MobxProps = {
  allowRolesPerformance: boolean;
  condensedReplays: boolean;
  currentConversation: Conversation | null;
  currentConversationInitialUnreadCount: number;
  currentMessageMultiSelectOption: string;
  currentPreviewConversation: Conversation | null;
  currentUserId: string | null;
  events: EventEmitter;
  fetchTimeline: (
    conversationId: string,
    {
      anchorPoint,
      continuation,
      maxItems,
    }: {
      anchorPoint?: unknown;
      continuation?: unknown;
      maxItems?: unknown;
    }
  ) => Promise<number | undefined>;
  fetchTimelineSelectedConversation: (
    conversation: Conversation,
    {
      anchorPoint,
      markAsRead,
    }?: {
      anchorPoint?: string | undefined;
      markAsRead?: boolean | undefined;
    }
  ) => Promise<void>;
  hasElement: (elementName: string) => boolean;
  hasMoreTimelineToShowBottom: (numOfMessagesToShow: number) => boolean;
  hasMoreTimelineToShowTop: (numOfMessagesToShow: number) => boolean;
  isGroupAlertsVCAllowed: boolean;
  isLoadingConversation: boolean;
  isProviderNetwork: boolean;
  messagesToShow: number;
  resetMessagesToShow: () => void;
  scrollOffset: number;
  scrollToElement: (elementName: string, scrollOptions?: unknown) => void;
  selectedMessageInitialOffset: number;
  setMessagesToShow: (messagesToShow: number) => void;
  setScrollbars: (ref: unknown) => void;
  setShouldShowLimitedMessages: (shouldShowLimitedMessages: boolean) => void;
  setStickyFirstUnreadMessage: (firstUnreadMessage: Message | null) => void;
  shouldMarkConversationAsRead: boolean;
  shouldShowLimitedMessages: boolean;
  showLoadingDots: boolean;
  startSentryTransaction: (name: string) => Sentry.Span | never;
  stickyFirstUnreadMessage: Message;
  setKeywordSearchScrollTarget: (element: HTMLElement | null) => void;
  isTaskManagerEnabled: boolean;
  loadingImagesInProgress: boolean;
};

type ConversationMessagesProps = {
  counterParty: Group;
  firstUnreadMessage: Message;
  lastMessage: Message;
  markConversationAsRead: (options: unknown) => Promise<void>;
  messages: Message[];
  isKeywordSearchModeOn: boolean;
  selectedMessageId: string;
  isGoToMessageNavigation: boolean;
  setSelectedMessageId: (messageId: string) => void;
  setSelectedHighlightedMessageId: (messageId: string) => void;
  setIsGoToMessageNavigation: (isGoToMessageNavigation: boolean) => void;
  gotoMessageNavigationEvent: boolean;
  setGotoMessageNavigationEvent: (gotoMessageNavigationEvent: boolean) => void;
};

type CombinedProps = ConversationMessagesProps & MobxProps;

type ConversationMessagesState = {
  numMessagesToShowTop: number;
  numMessagesToShowBottom: number;
};

class ConversationMessages extends Component<CombinedProps, ConversationMessagesState> {
  _addedContentToTop = false;
  _differentLastMessage = false;
  _enableTransition = false;
  _enableTransitionTimeout: number | null = null;
  _firstRenderLastMessage: unknown | null = null;
  _isFirstRender = true;
  _lastMessage: Message | null = null;
  _lastMessageCount = 0;
  _lastMessagesToShow: number | null = null;
  _lostStickiness = false;
  _messagesLimitedTimeout: number | null = null;
  _shouldLoseStickiness = false;
  _shouldMarkAsRead = false;
  _wasLoadingConversation = false;
  _wasScrolledToBottomBeforeRender = false;

  scroller: ScrollerStickToBottom | null = null;
  state = {
    numMessagesToShowTop: this.props.messagesToShow,
    numMessagesToShowBottom: this.props.messagesToShow,
  };
  _isMounted = false;
  _scrollRequestId = 0;
  _handledMessageId: string | null = null;
  _highlightTimeout: ReturnType<typeof setTimeout> | null = null;
  _clearGoToMessageTimeout: ReturnType<typeof setTimeout> | null = null;

  componentDidMount() {
    const { events, setStickyFirstUnreadMessage, stickyFirstUnreadMessage } = this.props;
    const { firstUnreadMessage } = this.props;

    this._isMounted = true;
    events.on('clearNewMessagesIndicator', this._clearNewMessagesIndicator);
    events.on('allImagesLoaded', this._onImagesLoaded);

    if (firstUnreadMessage && !stickyFirstUnreadMessage) {
      setStickyFirstUnreadMessage(firstUnreadMessage);
    }

    this._enableTransitionTimeout = setTimeout(() => {
      this._enableTransition = true;
      this._shouldMarkAsRead = true;
    }, AUTOSCROLL_AFTER_MOUNT_MS);

    this._scrollToSelectedMessageIfNeeded();
  }

  componentWillUnmount() {
    const { events, resetMessagesToShow } = this.props;
    this._isMounted = false;
    events.removeListener('clearNewMessagesIndicator', this._clearNewMessagesIndicator);
    events.removeListener('allImagesLoaded', this._onImagesLoaded);

    resetMessagesToShow();
    this._enableTransitionTimeout && clearTimeout(this._enableTransitionTimeout);
    this._messagesLimitedTimeout && window.cancelAnimationFrame(this._messagesLimitedTimeout);
    if (this._highlightTimeout) clearTimeout(this._highlightTimeout);
    if (this._clearGoToMessageTimeout) clearTimeout(this._clearGoToMessageTimeout);
    // Leaving the conversation (unmount) must also drop the "go to message" highlight gate —
    // otherwise it stays true and re-highlights selectedHighlightedMessageId on the next
    // conversation this message happens to render in.
    if (this.props.gotoMessageNavigationEvent) this.props.setGotoMessageNavigationEvent(false);
  }

  componentDidUpdate(prevProps: CombinedProps) {
    const skipScrollOnUpdate =
      this.props.isKeywordSearchModeOn || this.props.isGoToMessageNavigation;

    this._scrollOnUpdate(prevProps, skipScrollOnUpdate);

    if (!skipScrollOnUpdate) {
      if (
        this.scroller &&
        (prevProps.showLoadingDots || prevProps.isLoadingConversation) &&
        !(this.props.showLoadingDots || this.props.isLoadingConversation)
      ) {
        const element = ReactDOM.findDOMNode(this.scroller) as HTMLElement;
        if (element) element.focus();
      }
    }
    this._scrollToSelectedMessageIfNeeded();
  }

  // Selected message id is set by callers (e.g. keyword search, task "go to message") that may
  // unmount before the conversation finishes loading, so the scroll must live here, in the
  // component that actually renders the target conversation, instead of the caller.
  _scrollToSelectedMessageIfNeeded = async () => {
    const {
      selectedMessageId,
      isGoToMessageNavigation,
      isLoadingConversation,
      loadingImagesInProgress,
      currentConversation,
      fetchTimelineSelectedConversation,
    } = this.props;
    if (
      !selectedMessageId ||
      !isGoToMessageNavigation ||
      isLoadingConversation ||
      loadingImagesInProgress ||
      this._handledMessageId === selectedMessageId
    ) {
      return;
    }

    // Runs on every componentDidUpdate with no debounce, so a re-render can fire this again
    // before a prior call's awaits resolve (e.g. from ScrollerStickToBottom's own updates below,
    // or selectedMessageId changing mid-flight). requestId is a monotonic token: only the most
    // recently started call is "live" — isStale() catches every earlier one, regardless of
    // whether it matches the current selectedMessageId or was still in its first await.
    const requestId = ++this._scrollRequestId;
    const isStale = () => !this._isMounted || requestId !== this._scrollRequestId;

    // Same mechanism as keyword search (KeywordMessageBubble): by the time isLoadingConversation
    // is false, navigateToConversation's single sortNumber-anchored fetchTimelineSelectedConversation
    // call has already resolved — one waitForElement check, no retry/paging.
    let el = await waitForElement(`[data-message-id="${selectedMessageId}"]`);

    // navigateToConversation skips its own fetch when the conversation entity didn't exist yet
    // at call time (its getById lookup bails silently), so the target message never loads.
    // Retry once here, scoped to this "go to message" flow only — doesn't touch the shared
    // navigateToConversation path used by keyword search and other callers.
    if (!el && !isStale() && currentConversation) {
      await fetchTimelineSelectedConversation(currentConversation);
      if (!isStale()) {
        el = await waitForElement(`[data-message-id="${selectedMessageId}"]`);
      }
    }

    if (isStale()) return;

    // Marks this messageId as already scrolled-to so a re-render triggered from here on (e.g. the
    // setState below, widening numMessagesToShowTop) doesn't re-run this whole effect and re-fire
    // scrollIntoView on every subsequent update for as long as selectedMessageId stays set —
    // which previously fought every manual scroll during that window.
    this._handledMessageId = selectedMessageId;
    if (!el) {
      this.props.setSelectedMessageId('');
      this.props.setIsGoToMessageNavigation(false);
      return;
    }
    const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({
      behavior: isReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
    this.scroller?.markScrolledToElement();

    // Once selectedMessageId clears below, getLimitedMessages drops its targetMessageId window
    // and falls back to its default branch — the last numMessagesToShowTop messages counted from
    // the end. If numMessagesToShowTop isn't widened to reach this (possibly much older) message,
    // that fallback silently drops it from the render, which looks exactly like the view snapping
    // back to the most recent messages even though nothing scrolled.
    //
    // The widened value must reproduce the exact window already rendered right now (target branch:
    // messages.slice(max(0, targetIdx - numMessagesToShowTop))), not just "targetIdx is included" —
    // setting it any wider changes the window's start, which prepends/removes content the moment
    // the flags clear and causes its own scroll jump (observed: clamped to the bottom on shrink).
    const { messages } = this.props;
    const targetIdx = messages.findIndex((msg) => msg.serverId === selectedMessageId);
    if (targetIdx !== -1) {
      const currentWindowStartIdx = Math.max(0, targetIdx - this.state.numMessagesToShowTop);
      const neededTop = messages.length - currentWindowStartIdx;
      this.setState((prevState) => ({
        numMessagesToShowTop: Math.max(prevState.numMessagesToShowTop, neededTop),
      }));
    }

    // preventAutoScroll on ScrollerStickToBottom is driven by selectedMessageId/
    // isGoToMessageNavigation — clearing them immediately reopens the scroller's own
    // sticky-bottom auto-scroll while the smooth scrollIntoView animation is still running,
    // which wins the race and resets the view to the bottom. Wait out the animation first.
    if (this._clearGoToMessageTimeout) clearTimeout(this._clearGoToMessageTimeout);
    this._clearGoToMessageTimeout = setTimeout(
      () => {
        if (!this._isMounted) return;
        this._handledMessageId = null;
        this.props.setSelectedMessageId('');
        this.props.setIsGoToMessageNavigation(false);
      },
      isReducedMotion ? 0 : 500
    );

    if (this._highlightTimeout) clearTimeout(this._highlightTimeout);
    this._highlightTimeout = setTimeout(() => {
      this.props.setSelectedHighlightedMessageId('');
      this.props.setGotoMessageNavigationEvent(false);
    }, SELECTED_MESSAGE_HIGHLIGHT_MS);
  };

  render() {
    const {
      allowRolesPerformance,
      condensedReplays,
      counterParty,
      currentConversationInitialUnreadCount,
      hasMoreTimelineToShowBottom,
      hasMoreTimelineToShowTop,
      isGroupAlertsVCAllowed,
      isLoadingConversation,
      isProviderNetwork,
      lastMessage,
      messages,
      messagesToShow,
      scrollOffset,
      shouldShowLimitedMessages,
      showLoadingDots,
      stickyFirstUnreadMessage,
    } = this.props;
    const { numMessagesToShowTop, numMessagesToShowBottom } = this.state;
    const { patientDetails = {} } = counterParty || {};
    const { smsOptedOut = false } = patientDetails || {};
    let { firstUnreadMessage } = this.props;
    let itemsEstimateBottom = 0;
    let itemsEstimateTop = 0;
    let shouldFetchMoreTop = false;

    const conversation = this._getCurrentConversation();
    const showLoadingOverlay =
      showLoadingDots || isLoadingConversation || this.props.isGoToMessageNavigation;

    if (conversation) {
      const { allowedSenders, higherContinuation, lowerContinuation } = conversation;
      itemsEstimateBottom = higherContinuation.itemsEstimate;
      itemsEstimateTop = lowerContinuation.itemsEstimate;

      const isRoleFound = allowedSenders.some((entity) => entity.$entityType === 'role');

      const isFullyFetchedProp = isRoleFound ? 'fullyFetchedRole' : 'fullyFetchedUser';

      if (!condensedReplays && allowRolesPerformance) {
        shouldFetchMoreTop = !lowerContinuation[isFullyFetchedProp];
      }
    }

    if (stickyFirstUnreadMessage) {
      firstUnreadMessage = stickyFirstUnreadMessage;
    }

    const messageCount = messages.length;
    const isRole = !!counterParty && counterParty.p2pRecipientType === 'role';
    const isEscalationGroup = !!(
      counterParty &&
      counterParty.groupType === 'ESCALATION' &&
      counterParty.escalationExecution
    );
    const headerType = isEscalationGroup
      ? 'escalationGroup'
      : isRole
      ? 'role'
      : !isProviderNetwork
      ? 'patient'
      : 'normal';
    const moreContentAvailableBottom =
      itemsEstimateBottom > 0 ||
      (shouldShowLimitedMessages && hasMoreTimelineToShowBottom(numMessagesToShowBottom));
    const moreContentAvailableTop =
      itemsEstimateTop > 0 ||
      (shouldShowLimitedMessages && hasMoreTimelineToShowTop(numMessagesToShowTop)) ||
      shouldFetchMoreTop;

    let shownMessages;
    if (shouldShowLimitedMessages) {
      const sentryTransaction = this.props.startSentryTransaction(
        'ConversationMessages.getLimitedMessages'
      );

      shownMessages = getLimitedMessages(messages, {
        hasUnreadMessages: !!stickyFirstUnreadMessage,
        numMessagesToShowBottom,
        numMessagesToShowTop,
        unreadCount: currentConversationInitialUnreadCount,
        targetMessageId:
          this.props.isKeywordSearchModeOn || this.props.isTaskManagerEnabled
            ? this.props.selectedMessageId
            : '',
      });

      sentryTransaction?.end();
    } else {
      shownMessages = messages;
    }

    const differentLastMessage =
      (!this._lastMessage && !!lastMessage) || this._lastMessage !== lastMessage;
    const enableTransition = this._enableTransition && differentLastMessage;

    if (this._isFirstRender) {
      this._firstRenderLastMessage = lastMessage;
    }

    this._differentLastMessage = differentLastMessage;
    this._isFirstRender = false;
    this._lastMessage = lastMessage;
    this._lastMessageCount = messageCount;
    this._lastMessagesToShow = messagesToShow;
    this._lostStickiness = this._shouldLoseStickiness;
    this._shouldLoseStickiness = false;
    this._wasScrolledToBottomBeforeRender = this.isScrolledToBottom();

    const shouldShowGroupAlert =
      isGroupAlertsVCAllowed &&
      conversation?.featureService === 'group_alerts' &&
      conversation?.counterParty?.alertMessage;

    return (
      <div className={classes({ headerType, smsOptedOut })}>
        <ConversationTasksLoader
          conversation={conversation}
          enabled={this.props.isTaskManagerEnabled}
          messages={messages}
        />
        {shouldShowGroupAlert && (
          <div className={classes('alert-card-wrapper')}>
            <AlertCard
              message={conversation.counterParty.alertMessage}
              numLinesToClipContentAt={2}
            />
          </div>
        )}
        <div className={classes('message-list-wrapper')} id="message-list-wrapper">
          <ScrollerStickToBottom
            key={counterParty ? counterParty.id : 'new-message'}
            moreContentAvailableBottom={moreContentAvailableBottom}
            moreContentAvailableTop={moreContentAvailableTop}
            onScroll={this._onScroll}
            ref={this._setScroller}
            scrollOffset={scrollOffset}
            showMoreContent={this._increaseShownMessages}
            thumbMarginTop={THUMB_MARGIN_TOP}
            preventAutoScroll={
              !!(
                (this.props.isKeywordSearchModeOn || this.props.isTaskManagerEnabled) &&
                this.props.selectedMessageId
              )
            }
          >
            <GroupedMessagesList
              enableTransition={enableTransition}
              firstUnreadMessage={firstUnreadMessage}
              key={counterParty ? counterParty.id : 'new-message'}
              messages={shownMessages}
              showLoaderBottom={moreContentAvailableBottom}
              showLoaderTop={moreContentAvailableTop}
            />
          </ScrollerStickToBottom>
        </div>
        {showLoadingOverlay && (
          <div className={classes('loading-overlay')}>
            {(showLoadingDots ||
              this.props.isKeywordSearchModeOn ||
              this.props.isGoToMessageNavigation) && (
              <div className={classes('dots-container')}>
                <DotsIndicator color={'#969696'} size={13} />
                <div className={classes('dots-text')}>Loading conversation</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  _scrollOnUpdate = (prevProps: CombinedProps, skip = false) => {
    const {
      currentMessageMultiSelectOption,
      currentUserId,
      hasElement,
      isLoadingConversation,
      selectedMessageInitialOffset,
      scrollToElement,
      stickyFirstUnreadMessage,
    } = this.props;

    // While skipped (keyword search / go-to-message navigation own the scroll), still track
    // isLoadingConversation transitions so this._wasLoadingConversation isn't stale once the
    // guard lifts — a stale "still loading" value would read as a false "just finished loading"
    // transition below and force a scroll to bottom, undoing the go-to-message scroll.
    if (skip) {
      this._wasLoadingConversation = isLoadingConversation;
      return;
    }

    if (prevProps.currentMessageMultiSelectOption !== currentMessageMultiSelectOption) {
      scrollToElement('firstMessageSelected', {
        customOffset: selectedMessageInitialOffset - 135,
      });
      return;
    }

    const wasLoadingConversation = this._wasLoadingConversation;

    if (wasLoadingConversation && !isLoadingConversation) {
      if (hasElement(NEW_MESSAGE_INDICATOR)) {
        scrollToElement(NEW_MESSAGE_INDICATOR);
      } else {
        this._scrollToBottom();
      }
    } else if (
      this._lostStickiness ||
      (this._wasScrolledToBottomBeforeRender && !stickyFirstUnreadMessage)
    ) {
      this._scrollToBottom();
    } else if (
      this._lastMessage &&
      this._differentLastMessage &&
      this._lastMessage.senderId === currentUserId
    ) {
      this._scrollToBottom();
    }

    this._lostStickiness = false;
    this._wasLoadingConversation = isLoadingConversation;
    this._wasScrolledToBottomBeforeRender = false;
  };

  _markNewMessagesAsRead = ({ isScrolledToBottom }: { isScrolledToBottom?: boolean }) => {
    const { markConversationAsRead, shouldMarkConversationAsRead } = this.props;
    const conversation = this._getCurrentConversation();
    if (!conversation) return;

    if (
      shouldMarkConversationAsRead &&
      markConversationAsRead &&
      this._shouldMarkAsRead &&
      isScrolledToBottom &&
      document.hasFocus()
    ) {
      markConversationAsRead(conversation);
    }
  };

  _setScroller = (ref: ScrollerStickToBottom | null): void => {
    this.scroller = ref;
    this.props.setScrollbars(ref);
  };

  _isVisibleInContainer = (ele: Element, container: HTMLElement) => {
    const { bottom, height, top } = ele.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return top <= containerRect.top
      ? containerRect.top - top <= height
      : bottom - containerRect.bottom <= height;
  };

  _onScroll = ({
    direction,
    isScrolledToBottom,
    isUserScroll,
  }: {
    direction?: unknown;
    isScrolledToBottom?: boolean;
    isUserScroll?: boolean;
  }) => {
    if (isUserScroll && direction === 'DOWN') {
      this._markNewMessagesAsRead({ isScrolledToBottom });
    } else if (isUserScroll && direction === 'UP') {
      this._firstRenderLastMessage = null;
    }
  };

  _increaseShownMessages = async (direction: 'TOP' | 'BOTTOM') => {
    const skipped = await new Promise((resolve) => {
      if (this._messagesLimitedTimeout !== null) return resolve(true);
      resolve(false);
    });

    if (skipped) return;
    this._messagesLimitedTimeout = null;

    const { shouldShowLimitedMessages, setShouldShowLimitedMessages } = this.props;

    if (shouldShowLimitedMessages) {
      const { hasMoreTimelineToShowBottom, hasMoreTimelineToShowTop, messagesToShow } = this.props;
      const { numMessagesToShowTop, numMessagesToShowBottom } = this.state;
      const hasMoreTimelineTop = hasMoreTimelineToShowTop(numMessagesToShowTop);
      const hasMoreTimelineBottom = hasMoreTimelineToShowBottom(numMessagesToShowBottom);

      if (direction === 'TOP' && hasMoreTimelineTop) {
        return new Promise<void>((resolve) => {
          this.setState({ numMessagesToShowTop: numMessagesToShowTop + messagesToShow });
          resolve();
        });
      } else if (direction === 'BOTTOM' && hasMoreTimelineBottom) {
        return new Promise<void>((resolve) => {
          this.setState({ numMessagesToShowBottom: numMessagesToShowBottom + messagesToShow });
          resolve();
        });
      }

      if (!hasMoreTimelineTop && !hasMoreTimelineBottom) {
        setShouldShowLimitedMessages(false);
      }
    }

    const addedMessages =
      direction === 'TOP' ? await this._fetchMessagesTop() : await this._fetchMessagesBottom();

    return new Promise<void>((resolve) => {
      const { messagesToShow, setMessagesToShow } = this.props;
      setMessagesToShow(messagesToShow + addedMessages);
      resolve();
    });
  };

  _clearNewMessagesIndicator = (conversation: Conversation) => {
    const currentConversation = this._getCurrentConversation();
    if (currentConversation !== conversation) return;

    const { setStickyFirstUnreadMessage, stickyFirstUnreadMessage } = this.props;

    if (stickyFirstUnreadMessage) {
      this._shouldLoseStickiness = true;
      setStickyFirstUnreadMessage(null);
    }
  };

  isScrolledToBottom = () => {
    return this.scroller ? this.scroller.isScrolledToBottom() : false;
  };

  _scrollToBottom = () => {
    this.scroller && this.scroller.scrollToBottom();
  };

  _getCurrentConversation = () => {
    const { currentConversation, currentPreviewConversation } = this.props;
    return currentConversation || currentPreviewConversation;
  };

  _fetchMessagesTop = async () => {
    const conversation = this._getCurrentConversation();
    if (!conversation) return 0;

    const continuation = conversation.lowerContinuation.continuation;

    const addedMessages = await this._fetchTimeline({
      anchorPoint: 'CONTINUATION',
      continuation,
    });

    this._addedContentToTop = addedMessages > 0;

    return addedMessages;
  };

  _fetchMessagesBottom = async () => {
    const conversation = this._getCurrentConversation();
    if (!conversation) return 0;

    const continuation = conversation.higherContinuation.continuation;

    const addedMessages = await this._fetchTimeline({
      anchorPoint: 'CONTINUATION',
      continuation,
    });

    return addedMessages;
  };

  _fetchTimeline = async ({
    anchorPoint,
    continuation,
  }: {
    anchorPoint: string;
    continuation: unknown;
  }) => {
    const { fetchTimeline } = this.props;

    const conversation = this._getCurrentConversation();
    if (!conversation) return 0;

    const addedMessages = await fetchTimeline(conversation.id, {
      anchorPoint,
      continuation,
    });

    return addedMessages ?? 0;
  };

  _onImagesLoaded = () => {
    this._addedContentToTop = true;
    this.forceUpdate();
  };
}

const mapStateToProps = (state: ReduxState) => ({
  isKeywordSearchModeOn: state.keywordSearch.isKeywordSearchModeOn,
  selectedMessageId: state.keywordSearch.selectedMessageId,
  isGoToMessageNavigation: state.keywordSearch.isGoToMessageNavigation,
  gotoMessageNavigationEvent: state.keywordSearch.gotoMessageNavigationEvent,
});

const mapDispatchToProps = {
  setSelectedMessageId,
  setSelectedHighlightedMessageId,
  setIsGoToMessageNavigation: actions.setIsGoToMessageNavigation,
  setGotoMessageNavigationEvent,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  mobxInjectSelect<ConversationMessagesProps, MobxProps>({
    composeMessageStore: ['currentPreviewConversation'],
    conversationStore: [
      'currentConversation',
      'currentConversationInitialUnreadCount',
      'events',
      'fetchTimeline',
      'fetchTimelineSelectedConversation',
      'hasElement',
      'hasMoreTimelineToShowBottom',
      'hasMoreTimelineToShowTop',
      'isLoadingConversation',
      'loadingImagesInProgress',
      'messagesToShow',
      'resetMessagesToShow',
      'scrollOffset',
      'scrollToElement',
      'selectedMessageInitialOffset',
      'setMessagesToShow',
      'setScrollbars',
      'setShouldShowLimitedMessages',
      'setStickyFirstUnreadMessage',
      'shouldMarkConversationAsRead',
      'shouldShowLimitedMessages',
      'showLoadingDots',
      'stickyFirstUnreadMessage',
      'setKeywordSearchScrollTarget',
    ],
    messengerStore: [
      'allowRolesPerformance',
      'condensedReplays',
      'currentMessageMultiSelectOption',
      'isGroupAlertsVCAllowed',
      'isTaskManagerEnabled',
    ],
    networkStore: ['isProviderNetwork'],
    sessionStore: ['currentUserId'],
    trackerStore: ['startSentryTransaction'],
  })(ConversationMessages)
);
